/**
 * Shared, origin-independent persistence.
 *
 * Every `oemstream_*` localStorage write is mirrored to a server-side state
 * store (dev: file via Vite middleware; production: Cloudflare KV via Pages
 * Functions), and the app hydrates from that canonical store before render.
 * All admins — on any machine, any port — share the same state.
 *
 * Concurrency:
 *  - Writes carry a `baseUpdatedAt` stamp (optimistic concurrency). If the
 *    server state moved on (another admin saved first), the server answers
 *    409 with its current state; the client rehydrates — server wins — and
 *    the UI refreshes so the next edit builds on current data.
 *  - On window focus, the client rehydrates if the server stamp changed, so
 *    an open tab picks up other admins' changes without a manual reload.
 *
 * In environments without the API (static hosting), calls fail silently and
 * the app uses localStorage only.
 */

const PREFIX = 'oemstream_';
const STATE_ENDPOINT = '/api/localstate';
const DEBOUNCE_MS = 800;

/** Fired after server state is applied locally so the UI can recompute. */
export const HYDRATED_EVENT = 'oemstream:hydrated';

interface StateEnvelope {
  updatedAt: string | null;
  data: Record<string, string>;
}

let installed = false;
let hydrating = false;
let timer: ReturnType<typeof setTimeout> | null = null;
let lastSnapshot = '';
let serverStamp: string | null = null;

/** Patch localStorage so every dashboard write is mirrored to the server. */
export function installPersistence(): void {
  if (installed) return;
  installed = true;

  const origSet = localStorage.setItem.bind(localStorage);
  const origRemove = localStorage.removeItem.bind(localStorage);

  localStorage.setItem = (key: string, value: string) => {
    origSet(key, value);
    if (!hydrating && key.startsWith(PREFIX)) scheduleSync();
  };
  localStorage.removeItem = (key: string) => {
    origRemove(key);
    if (!hydrating && key.startsWith(PREFIX)) scheduleSync();
  };

  // Pick up other admins' changes when the tab regains focus.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void refreshFromServer();
    }
  });
}

function scheduleSync(): void {
  if (timer) clearTimeout(timer);
  timer = setTimeout(() => { void syncNow(); }, DEBOUNCE_MS);
}

function localSnapshot(): string {
  const data: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith(PREFIX)) {
      data[key] = localStorage.getItem(key) ?? '';
    }
  }
  return JSON.stringify(data);
}

/** Apply a server envelope locally and notify the UI. */
function applyServerState(envelope: StateEnvelope): boolean {
  const incoming = JSON.stringify(envelope.data ?? {});
  if (incoming === lastSnapshot && envelope.updatedAt === serverStamp) return false;

  hydrating = true;
  try {
    for (const [key, value] of Object.entries(envelope.data ?? {})) {
      if (key.startsWith(PREFIX) && typeof value === 'string') {
        localStorage.setItem(key, value);
      }
    }
    // Remove keys the server no longer holds (deleted by another admin).
    const serverKeys = new Set(Object.keys(envelope.data ?? {}));
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(PREFIX) && !serverKeys.has(key)) {
        localStorage.removeItem(key);
      }
    }
  } finally {
    hydrating = false;
  }

  serverStamp = envelope.updatedAt;
  lastSnapshot = incoming;
  window.dispatchEvent(new CustomEvent(HYDRATED_EVENT));
  return true;
}

async function syncNow(): Promise<void> {
  const snapshot = localSnapshot();
  if (snapshot === lastSnapshot) return;

  try {
    const res = await fetch(STATE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ baseUpdatedAt: serverStamp, data: JSON.parse(snapshot) }),
    });

    if (res.ok) {
      const body = await res.json().catch(() => ({}));
      serverStamp = body.updatedAt ?? serverStamp;
      lastSnapshot = snapshot;
    } else if (res.status === 409) {
      // Another admin saved first — take the server state, refresh the UI.
      const envelope = (await res.json()) as StateEnvelope;
      applyServerState(envelope);
    }
  } catch {
    // No API reachable — localStorage-only mode.
  }
}

/**
 * Load the canonical server state into localStorage before the app renders.
 */
export async function hydrateFromServer(): Promise<void> {
  try {
    const res = await fetch(STATE_ENDPOINT);
    if (!res.ok) return;
    const envelope = (await res.json()) as StateEnvelope;
    if (!envelope || typeof envelope !== 'object' || !('data' in envelope)) return;
    applyServerState(envelope);
  } catch {
    // No API reachable — start from localStorage as-is.
  }
}

/** Rehydrate when the server stamp has moved (called on window focus). */
async function refreshFromServer(): Promise<void> {
  try {
    const res = await fetch(STATE_ENDPOINT);
    if (!res.ok) return;
    const envelope = (await res.json()) as StateEnvelope;
    if (!envelope || typeof envelope !== 'object' || !('data' in envelope)) return;
    if (envelope.updatedAt !== serverStamp) {
      applyServerState(envelope);
    }
  } catch {
    // ignore
  }
}
